import { readActivePage, rememberActivePage, forgetActivePage } from './lib/activePage'
import PagePermissionActions, { PagePermissionProvider } from './components/PagePermissionActions'
import { clearSystemDialogs } from './lib/systemDialogs'
import { invalidateSharedReads } from './lib/sharedRead'
import { recoverablePage as lazyPage } from './lib/recoverablePage'
import PageErrorBoundary from './components/PageErrorBoundary'
import { requestPageRefresh } from './lib/usePageRefresh'
import SystemTabs from './components/SystemTabs'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import AppShell from './components/AppShell'
import EmployeeProfileModal from './components/EmployeeProfileModal'
import PayrollTabs from './components/PayrollTabs'
import RulesTabs from './components/RulesTabs'
import LongLeaveAdminPanel from './components/LongLeaveAdminPanel'
import ProfileCompletionReminder from './components/ProfileCompletionReminder'
import LoginPage from './pages/LoginPage'
import LeaveListPersonalStats from './pages/LeaveListPersonalStats'
import LeaveListTypeColumn from './pages/LeaveListTypeColumn'
import LeaveRegistrationEnhancements from './pages/LeaveRegistrationEnhancements'
import EmployeeManagementEnhancements from './pages/EmployeeManagementEnhancements'
import EmployeeExactSearch from './pages/EmployeeExactSearch'
import TourAdminCustomerCount from './pages/TourAdminCustomerCount'
import { veraApi } from './lib/api'
import { ensureGrantedPushSubscription, setPushAccount } from './lib/pushNotifications'
import { getCurrentSession, isAuthConfigured, onVeraAuthStateChange, signOutVera } from './lib/supabase'


const VALID_PAGES = new Set(['online-bookings', 'system', 'hr', 'leave', 'schedule', 'long-leave', 'residence', 'employees', 'contract-1', 'rules', 'hc-rules', 'profile', 'permissions', 'payroll', 'department-payroll', 'payroll-config', 'revenue', 'quang-ngai', 'purchases', 'training', 'snapshot', 'devices', 'checkin-history', 'birthday', 'tour', 'live-tour', 'milk-tea', 'reports', 'customers', 'settings', 'appearance', 'notifications', 'auto-check', 'changes', 'storage'])

const readNotificationPage = () => new URLSearchParams(window.location.search).get('page') === 'changes' ? 'changes' : ''

const readStandalonePageRequest = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    const requestedPage = params.get('page')
    return {
      enabled: params.get('standalone') === '1' && VALID_PAGES.has(requestedPage),
      page: VALID_PAGES.has(requestedPage) ? requestedPage : '',
    }
  } catch {
    return { enabled: false, page: '' }
  }
}

const OnlineBookingPage = lazyPage(() => import('./pages/OnlineBookingPage'))
const PurchasePage = lazyPage(() => import('./pages/PurchasePage'))

const LeaveRegistrationPage = lazyPage(() => import('./pages/LeaveRegistrationPage'))
const HumanResourcesPage = lazyPage(() => import('./pages/HumanResourcesPage'))
const ResidencePage = lazyPage(() => import('./pages/ResidencePage'))
const EmployeePage = lazyPage(() => import('./pages/EmployeePage'))
const HcRulesPage = lazyPage(() => import('./pages/HcRulesPage'))
const RulesPage = lazyPage(() => import('./pages/RulesPage'))
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'))
const PermissionsPage = lazyPage(() => import('./pages/PermissionsPage'))
const PayrollPage = lazyPage(() => import('./pages/PayrollPageV38'))
const QuangNgaiPage = lazyPage(() => import('./pages/QuangNgaiPage'))
const RevenuePage = lazyPage(() => import('./pages/RevenuePage'))
const AttendancePage = lazyPage(() => import('./pages/AttendancePage'))
const DevicePage = lazyPage(() => import('./pages/DevicePage'))
const AdminChangesPage = lazyPage(() => import('./pages/AdminChangesPage'))
const StorageAdminPage = lazyPage(() => import('./pages/StorageAdminPage'))
const BirthdayPage = lazyPage(() => import('./pages/BirthdayPage'))
const TourPage = lazyPage(() => import('./pages/TourPage'))
const LiveTourReportsPage = lazyPage(() => import('./pages/LiveTourReportsPage'))
const LiveTourPage = lazyPage(() => import('./pages/LiveTourPage'))
const MilkTeaPage = lazyPage(() => import('./pages/MilkTeaPage'))
const SpaManagementPage = lazyPage(() => import('./pages/SpaManagementPage'))
const AutoCheckPage = lazyPage(() => import('./pages/AutoCheckPage'))
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'))
const AppearanceSettingsPage = lazyPage(() => import('./pages/AppearanceSettingsPage'))
const NotificationSettingsPage = lazyPage(() => import('./pages/NotificationSettingsPage'))
const LongLeaveSection = lazyPage(() => import('./components/LongLeaveSection'))
const WorkSchedulePage = lazyPage(() => import('./pages/WorkSchedulePage'))
const DepartmentPayrollSettingsPage = lazyPage(() => import('./pages/DepartmentPayrollSettingsPage'))
const DepartmentPayrollPanel = lazyPage(() => import('./pages/DepartmentPayrollPanel'))
const ContractPage = lazyPage(() => import('./pages/ContractPage'))
const TrainingPage = lazyPage(() => import('./pages/TrainingPage'))
const pageModules = {
  'online-bookings': OnlineBookingPage,
  leave: LeaveRegistrationPage, schedule: WorkSchedulePage, 'long-leave': LongLeaveSection,
  residence: ResidencePage, employees: EmployeePage, 'contract-1': ContractPage, rules: RulesPage, 'hc-rules': HcRulesPage, profile: ProfilePage,
  hr: HumanResourcesPage, payroll: PayrollPage, 'department-payroll': DepartmentPayrollPanel,
  'payroll-config': DepartmentPayrollSettingsPage, revenue: RevenuePage, 'quang-ngai': QuangNgaiPage, purchases: PurchasePage,
  training: TrainingPage, snapshot: AttendancePage, devices: DevicePage, 'checkin-history': AttendancePage,
  birthday: BirthdayPage, tour: TourPage, reports: LiveTourReportsPage, 'live-tour': LiveTourPage,
  'milk-tea': MilkTeaPage, customers: SpaManagementPage, settings: SettingsPage,
  notifications: NotificationSettingsPage, permissions: PermissionsPage, 'auto-check': AutoCheckPage,
  system: AdminChangesPage, changes: AdminChangesPage, storage: StorageAdminPage,
}
const preloadPage = page => { void pageModules[page]?.preload().catch(() => {}) }

export default function App() {
  const verifiedUser = useRef(null)
  const [standaloneRequest] = useState(readStandalonePageRequest)
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(isAuthConfigured)
  const [authError, setAuthError] = useState('')
  const [sessionRecoveryError, setSessionRecoveryError] = useState(false)
  const [authRetry, setAuthRetry] = useState(0)
  const [page, setPage] = useState('leave')
  const [longLeaveRevision, setLongLeaveRevision] = useState(0)
  const employeeNavigationRef = useRef(null)
  const registerEmployeeNavigation = useCallback((getState) => { employeeNavigationRef.current = getState }, [])
  const [pendingEmployeePage, setPendingEmployeePage] = useState('')
  const [savingEmployeeNavigation, setSavingEmployeeNavigation] = useState(false)
  const [employeeNavigationError, setEmployeeNavigationError] = useState('')

  useEffect(() => {
    if (!isAuthConfigured) return undefined
    let mounted = true
    let verification = 0
    setLoading(true)
    setProfile(null)
    setSessionRecoveryError(false)
    const applySession = async (nextSession) => {
      if (!mounted) return
      invalidateSharedReads()
      const attempt = ++verification
      setSession(nextSession); setAuthError(''); setSessionRecoveryError(false)
      if (!nextSession) { verifiedUser.current = null; setProfile(null); setLoading(false); return }
      setLoading(true)
      setProfile(null)
      try {
        // Verify the persisted token before opening business pages. The shared
        // API client refreshes a stale token once on 401, preventing a locally
        // cached session from opening an app where every data table is blank.
        const me = await veraApi.me()
        if (!me?.employee_username || me?.is_active === false) {
          const error = new Error('Tài khoản chưa được liên kết với nhân viên VERA đang hoạt động.')
          error.status = 403
          throw error
        }
        if (mounted && attempt === verification) {
          setProfile(me)
          if (me.must_change_password || verifiedUser.current !== nextSession.user.id) {
            setPage(me.must_change_password ? 'profile' : standaloneRequest.enabled ? standaloneRequest.page : readNotificationPage() || readActivePage(nextSession.user, VALID_PAGES))
          }
          verifiedUser.current = nextSession.user.id
        }
      } catch (err) {
        if (mounted && attempt === verification) {
          setProfile(null)
          setAuthError(err.message || 'Không xác minh được hồ sơ VERA.')
          // A database outage must not revoke a valid session. Business pages
          // remain gated by profile verification until the operator retries.
          if (err.status === 401 || err.status === 403) {
            await signOutVera()
            if (mounted && attempt === verification) setSession(null)
          }
        }
      } finally { if (mounted && attempt === verification) setLoading(false) }
    }
    getCurrentSession().then(applySession).catch((err) => {
      // A transient refresh error on startup is recoverable. Do not open the
      // app from cached profile data or force the operator to log in again.
      if (mounted && verification === 0) {
        setSessionRecoveryError(true)
        setAuthError(err.message || 'Không mở được phiên đăng nhập VERA.')
        setLoading(false)
      }
    })
    const unsubscribe = onVeraAuthStateChange((_event, nextSession) => applySession(nextSession))
    return () => { mounted = false; unsubscribe() }
  }, [standaloneRequest, authRetry])

  // Every authenticated account keeps an already-approved Web Push endpoint
  // synchronized with the backend. This is especially important on iPhone:
  // once the Home Screen PWA has Notification.permission=granted, the endpoint
  // is recreated/re-registered without another prompt and remains usable for
  // lock-screen push while the app is not in the foreground.
  useEffect(() => {
    if (loading) return undefined
    if (!session?.access_token || !profile?.employee_username || profile?.must_change_password) {
      void setPushAccount('')
      return undefined
    }
    void setPushAccount(session.user?.id || '')
    let stopped = false
    let running = false
    const syncPush = async () => {
      if (stopped || running) return
      running = true
      try { await ensureGrantedPushSubscription() } catch { /* notification sync must not block the app */ }
      finally { running = false }
    }
    void syncPush()
    const timer = window.setInterval(syncPush, 10 * 60 * 1000)
    const onFocus = () => { void syncPush() }
    const onVisible = () => { if (document.visibilityState === 'visible') void syncPush() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped = true
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [loading, profile?.employee_username, profile?.must_change_password, session?.access_token, session?.user?.id])

  useEffect(() => () => clearSystemDialogs(), [session?.user?.id, profile?.employee_username])

  if (loading) return <div className="boot-screen">Đang mở VERA SPA…</div>
  const user = session?.user
  if (!user && !sessionRecoveryError) return <LoginPage externalError={authError} />

  const signOut = async () => { forgetActivePage(user); void setPushAccount(''); setProfile(null); await signOutVera() }
  const applyPageChange = (nextPage) => {
    if (standaloneRequest.enabled) {
      const url = new URL(window.location.href)
      url.searchParams.set('standalone', '1')
      url.searchParams.set('page', nextPage)
      window.history.replaceState({}, '', url)
    }
    if (!standaloneRequest.enabled && readNotificationPage()) {
      const url = new URL(window.location.href)
      url.searchParams.delete('page')
      window.history.replaceState({}, '', url)
    }
    rememberActivePage(user, nextPage, VALID_PAGES)
    setPage(nextPage)
  }
  const changePage = (nextPage) => {
    if (nextPage === page) return
    if (page === 'employees' && employeeNavigationRef.current?.()?.dirty) {
      setEmployeeNavigationError('')
      setPendingEmployeePage(nextPage)
      return
    }
    applyPageChange(nextPage)
  }
  const finishEmployeeNavigation = async (save) => {
    if (savingEmployeeNavigation || !pendingEmployeePage) return
    if (save) {
      setSavingEmployeeNavigation(true)
      setEmployeeNavigationError('')
      try {
        if (!await employeeNavigationRef.current?.()?.save()) {
          setEmployeeNavigationError('Chưa lưu được toàn bộ dữ liệu. Kiểm tra các trường cần nhập hoặc thông báo lỗi ở trang Nhân viên.')
          return
        }
      } catch (error) {
        setEmployeeNavigationError(error.message || 'Chưa lưu được dữ liệu nhân viên.')
        return
      } finally { setSavingEmployeeNavigation(false) }
    }
    const destination = pendingEmployeePage
    setPendingEmployeePage('')
    applyPageChange(destination)
  }
  const refreshCurrentPage = () => requestPageRefresh()

  const shellUser = profile ? {
    ...user,
    employee_username: profile.employee_username,
    role: profile.role,
    permissions: profile.permissions || {},
    registration_locked: Boolean(profile.registration_locked),
    must_change_password: Boolean(profile.must_change_password),
    user_metadata: { ...(user.user_metadata || {}), full_name: profile.full_name || profile.employee_username },
  } : null

  if (!shellUser) return <div className="boot-screen"><div><p role={authError ? "alert" : "status"}>{authError || 'Đang xác minh hồ sơ VERA SPA…'}</p><button type="button" className="primary-button" onClick={() => setAuthRetry(value => value + 1)}>Thử xác minh lại</button><button type="button" className="secondary-button" onClick={signOut}>Đăng xuất</button></div></div>

  return (
    <PagePermissionProvider user={shellUser} page={page}><AppShell user={shellUser} currentPage={page} standalone={standaloneRequest.enabled} onPageChange={changePage} onPageIntent={preloadPage} onRefreshCurrentPage={refreshCurrentPage} onSignOut={signOut}>
      {(navigationToggle) => <>
        {page !== 'live-tour' && page !== 'quang-ngai' && <PagePermissionActions primary page={page}/>}
        {page !== 'live-tour' && <ProfileCompletionReminder user={shellUser} onOpenProfile={() => changePage('profile')} />}
        <PageErrorBoundary key={page} page={page} onRetry={() => pageModules[page]?.reset()}>
        <Suspense fallback={<div className="page-loading" role="status">Đang mở chức năng…</div>} key={page}>
          {page === 'leave' && <><LeaveRegistrationPage user={shellUser} /><LeaveRegistrationEnhancements user={shellUser} /><LeaveListPersonalStats user={shellUser} /><LeaveListTypeColumn user={shellUser} /></>}
          {page === 'schedule' && <WorkSchedulePage user={shellUser} />}
          {page === 'long-leave' && <>
            {/* Canonical route shape retained for CI/history: <LongLeaveSection user={shellUser} /> */}
            <LongLeaveAdminPanel user={shellUser} onChanged={() => setLongLeaveRevision((value) => value + 1)} />
            <LongLeaveSection refreshRevision={longLeaveRevision} user={shellUser} />
        </>}
        {page === 'residence' && <ResidencePage user={shellUser} />}
        {page === 'employees' && <><EmployeePage user={shellUser} registerNavigationGuard={registerEmployeeNavigation} /><EmployeeManagementEnhancements user={shellUser} /><EmployeeExactSearch /></>}
        {page === 'contract-1' && <ContractPage user={shellUser} />}
        {(page === 'rules' || page === 'hc-rules') && <RulesTabs initialTab={page === 'hc-rules' ? 'administrative' : 'ktv'} administrative={<HcRulesPage user={shellUser} />} ktv={<RulesPage user={shellUser} />} />}
        {page === 'profile' && <ProfilePage user={shellUser} forcePasswordChange={shellUser.must_change_password} onPasswordChanged={signOut} />}
        {page === 'hr' && <HumanResourcesPage user={shellUser} />}
        {(page === 'payroll' || page === 'department-payroll' || page === 'payroll-config') && <PayrollTabs user={shellUser} initialTab={page === 'payroll-config' ? 'configuration' : page === 'department-payroll' ? 'administrative' : 'ktv'} ktv={<PayrollPage user={shellUser} />} administrative={<DepartmentPayrollPanel user={shellUser} />} configuration={<DepartmentPayrollSettingsPage user={shellUser} />} />}
        {page === 'revenue' && <RevenuePage user={shellUser} />}
        {page === 'quang-ngai' && <QuangNgaiPage user={shellUser} />}
        {page === 'purchases' && <PurchasePage user={shellUser} />}
        {page === 'training' && <TrainingPage user={shellUser} />}
        {page === 'snapshot' && <AttendancePage key="snapshot" user={shellUser} />}
        {page === 'devices' && shellUser.permissions?.device_view && <DevicePage user={shellUser} />}
        {page === 'checkin-history' && <AttendancePage key="checkin-history" user={shellUser} initialTab="history" />}
        {page === 'birthday' && <BirthdayPage />}
        {page === 'tour' && <><TourPage user={shellUser} /><TourAdminCustomerCount user={shellUser} /></>}
        {page === 'reports' && <LiveTourReportsPage user={shellUser} />}
        {page === 'online-bookings' && <OnlineBookingPage user={shellUser} />}
        {page === 'live-tour' && <LiveTourPage user={shellUser} navigationToggle={navigationToggle} />}
        {page === 'milk-tea' && <MilkTeaPage user={shellUser} />}
        {page === 'customers' && <SpaManagementPage user={shellUser} mode="customers" />}
        {page === 'appearance' && <p>Giao diện đã chuyển vào menu Chỉnh bố cục: chọn Bảng &amp; cột hoặc Phòng Live Tour.</p>}
        {['settings', 'notifications', 'permissions'].includes(page) && <SettingsPage user={shellUser} initialTab={page === 'settings' ? undefined : page} notifications={<NotificationSettingsPage user={shellUser} />} permissionSettings={<PermissionsPage user={shellUser} />} />}
        {page === 'auto-check' && <AutoCheckPage user={shellUser} />}
        {['system', 'changes', 'storage'].includes(page) && <SystemTabs user={shellUser} initialTab={page === 'storage' ? 'storage' : 'changes'} changes={<AdminChangesPage user={shellUser} />} storage={<StorageAdminPage />} />}
      </Suspense>
      </PageErrorBoundary>
      {pendingEmployeePage && <EmployeeProfileModal labelledBy="employee-navigation-title" busy={savingEmployeeNavigation} onClose={() => setPendingEmployeePage('')}>
        <section className="panel employee-navigation-dialog">
          <h2 id="employee-navigation-title">Thay đổi nhân viên chưa lưu</h2>
          <p>Bạn muốn lưu dữ liệu trước khi chuyển menu?</p>
          {employeeNavigationError && <p role="alert" className="employee-navigation-error">{employeeNavigationError}</p>}
          <div className="employee-navigation-actions">
            <button type="button" className="secondary-button" disabled={savingEmployeeNavigation} onClick={() => setPendingEmployeePage('')}>Tiếp tục chỉnh sửa</button>
            <button type="button" className="danger-button" disabled={savingEmployeeNavigation} onClick={() => void finishEmployeeNavigation(false)}>Không lưu</button>
            <button type="button" className="primary-button" disabled={savingEmployeeNavigation} onClick={() => void finishEmployeeNavigation(true)}>{savingEmployeeNavigation ? 'Đang lưu…' : 'Lưu và chuyển'}</button>
          </div>
        </section>
      </EmployeeProfileModal>}
      </>}
    </AppShell></PagePermissionProvider>
  )
}
